import {
  Divider,
  Stack,
  type StackProps,
  styled,
  Typography,
  type TypographyProps,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

//TODO: fix this types errors
export type ComparableType = "PTO" | "3M" | "MoM" | "YoY" | "QoQ";
export type TemporalidadType = "Mensual" | "Trimestral" | "Anual";
export type DivisaType =
  | "MXN"
  | "EUR"
  | "CAD"
  | "BRL"
  | "COP"
  | "HNL"
  | "ARS"
  | "CLP"
  | "PEN"
  | "USD";

export type UnitType = "K" | "M" | "B";

export type DecimalsType = "0" | ".0" | ".00";

export type PeriodicityType = "Mensual" | "Trimestral" | "Anual";

export const COMPARABLE_OPTIONS: { value: ComparableType; label: string }[] = [
  { value: "3M", label: "Promedio 3M" },
  { value: "MoM", label: "Mes anterior (MoM)" },
  { value: "QoQ", label: "Trim anterior (QoQ)" },
  { value: "YoY", label: "Año anterior (YoY)" },
  { value: "PTO", label: "Presupuesto (PTO)" },
];
export const TEMPORALIDAD_OPTIONS: {
  value: TemporalidadType;
  label: string;
}[] = [
  { value: "Mensual", label: "Mes (M)" },
  { value: "Trimestral", label: "Trimestre (Q)" },
  { value: "Anual", label: "Año (LTM)" },
];
export const DIVISA_OPTIONS = [
  { value: "MXN", label: "Peso mexicano (MXN)" },
  { value: "USD", label: "Dólar americano (USD)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "CAD", label: "Dólar canadiense (CAD)" },
  { value: "BRL", label: "Real brasileño (BRL)" },
  { value: "COP", label: "Peso colombiano (COP)" },
  { value: "HNL", label: "Lempira hondureña (HNL)" },
  { value: "ARS", label: "Peso argentino (ARS)" },
  { value: "CLP", label: "Peso chileno (CLP)" },
  { value: "PEN", label: "Sol peruano (PEN)" },
];

export const UNIDADES_OPTIONS: { value: UnitType; label: string }[] = [
  { value: "K", label: "Miles (K)" },
  { value: "M", label: "Millones (M)" },
  { value: "B", label: "Mil millones (B)" },
];

// biome-ignore lint/style/useNamingConvention: Union types cannot be interfaces
export type TitleColor = "primary" | "secondary";

/**
 * Filter values that TitleWithFilters needs to display
 */
export interface FilterDisplayValues {
  comparable: string;
  temporalidad: string;
  periodo: string;
  divisa: string;
  unidades: UnitType;
}

export interface TitleWithFiltersProps extends StackProps {
  title: string;
  subtitle?: string;
  /**
   * Color for the title. Affects the color of the title, the divider and the icon.
   * @default "primary"
   */
  color?: TitleColor;
  icon?: React.ReactNode;
  /**
   * Variant of the Typography used for the title.
   * @default "h5"
   */
  titleVariant?: TypographyProps["variant"];
  /**
   * Hook that returns the filter values to display.
   * Alternative to passing individual filter props.
   */
  useFilters?: () => FilterDisplayValues;
  /**
   * Individual filter values - use these as an alternative to useFilters.
   * These allow the component to be reused across different contexts (Resultados, Flujo, Balance)
   */
  comparable?: string;
  temporalidad?: string;
  periodo?: string;
  divisa?: string;
  unidades?: UnitType;
  /**
   * When provided, replaces the `divisa` and `unidades` filters with this single label.
   */
  unit?: string;
  sx?: SxProps<Theme>;
}

const StyledDivider = styled(Divider, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: TitleColor }>(({ color, theme }) => ({
  width: "1px",
  height: "18px",
  border: 1,
  backgroundColor: theme.palette[color].dark,
}));

export function TitleWithFilters({
  title,
  subtitle,
  color = "primary",
  icon,
  titleVariant = "h5",
  useFilters,
  comparable: comparableProp,
  temporalidad: temporalidadProp,
  periodo: periodoProp,
  divisa: divisaProp,
  unidades: unidadesProp,
  unit,
  ...props
}: TitleWithFiltersProps) {
  // Get values from hook if provided (must call hooks unconditionally)
  const filterValues = useFilters?.();

  // Use props if provided, otherwise fall back to hook values
  const comparable = comparableProp ?? filterValues?.comparable ?? "";
  const temporalidad = temporalidadProp ?? filterValues?.temporalidad ?? "";
  const periodo = periodoProp ?? filterValues?.periodo ?? "";
  const divisa = divisaProp ?? filterValues?.divisa ?? "";
  const unidades = unidadesProp ?? filterValues?.unidades ?? "M";

  const currentTemporalidadLabel =
    TEMPORALIDAD_OPTIONS.find((opt) => opt.value === temporalidad)?.label ||
    temporalidad;

  const currentComparableLabel =
    COMPARABLE_OPTIONS.find((opt) => opt.value === comparable)?.label ||
    comparable;

  const currentUnidadesLabel =
    UNIDADES_OPTIONS.find((opt) => opt.value === unidades)?.label || unidades;

  const filterParts = unit
    ? [currentTemporalidadLabel, periodo, currentComparableLabel, unit]
    : [
        currentTemporalidadLabel,
        periodo,
        currentComparableLabel,
        divisa,
        currentUnidadesLabel,
      ];

  return (
    <Stack display="flex" gap={1} alignItems="flex-start" {...props}>
      <Stack
        direction={"row"}
        alignItems="center"
        gap={0.75}
        sx={(theme) => ({
          color:
            "primary" === color
              ? theme.palette.primary.light
              : theme.palette.secondary.light,
        })}
      >
        {icon || null}
        <Typography
          variant={titleVariant}
          sx={(theme) => ({
            color:
              "primary" === color
                ? theme.palette.primary.main
                : theme.palette.secondary.dark,
          })}
        >
          {title}
        </Typography>
        {subtitle && <StyledDivider color={color} />}
        <Typography
          variant="body1"
          sx={(theme) => ({
            color: theme.palette.secondary.dark,
            fontWeight: subtitle
              ? theme.typography.fontWeightSemiBold
              : theme.typography.fontWeightMedium,
          })}
        >
          {subtitle}
        </Typography>
      </Stack>
      <Typography variant="overline">
        {filterParts.filter((v) => v).join(" | ")}
      </Typography>{" "}
    </Stack>
  );
}
